import React, { FC, useState, useCallback } from 'react';
import {
    IconButton,
    InputAdornment,
    SxProps,
    TextField,
    TextFieldProps,
    Theme,
    formHelperTextClasses,
    inputBaseClasses,
} from '@mui/material';
import { FieldRenderProps } from 'react-final-form';
import InputMask from 'react-input-mask';
import { forms } from '@factorin/container.macro';

import { multipleRef } from 'Modules/ui';

import { useInputOnChange } from '../../hooks';
import { Eye24, EyeClose24, MiniClose24 } from '../../icons';


const pyBySize: Record<InputProps['size'], number> = {
    large: 4,
    medium: 3,
};

const paddingBySize: Record<InputProps['size'], { pt: number, pb: number}> = {
    large: {
        pt: 6,
        pb: 2,
    },
    medium: {
        pt: 5,
        pb: 1,
    },
};


export type InputProps = Omit<TextFieldProps, 'variant' | 'onChange' | 'size'> & {
    onChange: (value: string) => void;
    needEvent?: boolean;
    mask?: string;
    formatChars?: any;
    suffix?: React.ReactNode;
    value?: string;
    noLabel?: boolean;
    size?: 'large' | 'medium';
    specialHelperSx?: SxProps<Theme>;
    helperBlockAlwaysShow?: boolean;
    maxLength?: number;
    clearable?: boolean;
};

const InputForwardRef = React.forwardRef<HTMLDivElement, InputProps
& { maskChar?: string, formatChars?: Record<string, string> }>(
    (
        {
            required,
            fullWidth = true,
            needEvent,
            value,
            onFocus,
            onBlur,
            onChange,
            onPaste,
            onMouseDown,
            inputProps,
            suffix,
            InputProps,
            disabled,
            noLabel,
            maskChar = null,
            formatChars,
            size = 'large',
            specialHelperSx,
            helperBlockAlwaysShow,
            maxLength,
            InputLabelProps = {},
            clearable,
            ...rest
        },
        ref,
    ) => {
        const change = useInputOnChange(onChange, needEvent);
        const showClearIcon = clearable && value;

        return (
            <InputMask
                maskChar={maskChar}
                formatChars={formatChars}
                onChange={change}
                sx={{
                    ...noLabel ? {
                        [`& .${inputBaseClasses.input}`]: {
                            py: pyBySize[size],
                            ...rest.error && {
                                color: 'infoCustom.red50',
                            },
                        },
                        [`& .${inputBaseClasses.root}+.${formHelperTextClasses.root}`]: {
                            marginLeft: 0,
                            marginTop: 2,
                        },
                    } : {
                        [`& .${inputBaseClasses.input}`]: {
                            ...paddingBySize[size],
                        },
                    },
                    ...specialHelperSx,
                }}
                variant='filled'
                fullWidth={fullWidth}
                value={value}
                onBlur={onBlur}
                onFocus={onFocus}
                inputProps={{
                    ref: multipleRef(ref, inputProps?.ref),
                    value,
                    onChange: change,
                    onFocus,
                    onBlur,
                    onPaste,
                    disabled,
                    maxLength,
                    sx: ({ spacing }) => ({
                        height: spacing(6),
                    }),
                    ...inputProps,
                }}
                InputLabelProps={{ required, disabled, ...InputLabelProps }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                    disabled,
                    endAdornment: suffix || showClearIcon && (
                        <InputAdornment
                            position='end'
                            sx={{
                                my: 4,
                                mr: 4,
                            }}
                        >{
                                suffix || (
                                    <MiniClose24
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={() => onChange(undefined)}
                                        sx={({ spacing }) => ({
                                            cursor: 'pointer',
                                            color: 'grayscale.shade95',
                                            fontSize: spacing(6),
                                        })}
                                    />
                                )
                            }
                        </InputAdornment>
                    ),
                    ...InputProps,
                }}
                onPaste={onPaste}
                disabled={disabled}
                {...rest}
            >
                {
                    // @ts-ignore
                    props => (
                        <TextField
                            {...props}
                            hiddenLabel={noLabel}

                            {...helperBlockAlwaysShow && {
                                helperText: props.helperText ?? ' ',
                            }}
                        />
                    )
                }
            </InputMask>
        );
    },
);

export const Input = React.memo(InputForwardRef);


type TextAreaProps = Omit<InputProps, 'mask' | 'formatChars' | 'size'>;

export const TextArea = React.forwardRef<HTMLDivElement, TextAreaProps>(
    (
        {
            required,
            fullWidth = true,
            needEvent,
            value,
            onFocus,
            onBlur,
            onChange,
            onPaste,
            onMouseDown,
            inputProps,
            suffix,
            InputProps,
            ...rest
        },
        ref,
    ) => (
        <TextField
            ref={multipleRef(ref, inputProps?.ref)}
            variant='filled'
            multiline
            fullWidth={fullWidth}
            value={value}
            onChange={useInputOnChange(onChange, needEvent)}
            onBlur={onBlur}
            onFocus={onFocus}
            inputProps={inputProps}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
                endAdornment: suffix && (
                    <InputAdornment position='end'>{suffix}</InputAdornment>
                ),
                ...InputProps,
            }}
            onPaste={onPaste}
            {...rest}
        />
    ),
);

export type InputFieldType = FieldRenderProps<any, HTMLElement, string> & InputProps;
export const InputField: FC<InputFieldType> = React.forwardRef(({
    input: { name, value, type, onChange, onBlur, onFocus, ...restInput },
    meta,
    helperText,
    showMaxSize,
    maxSize,
    onPaste,
    ...props
}, ref) => (
    <Input
        ref={ref}
        name={name}
        value={value}
        type={type}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        inputProps={restInput}
        helperText={
            (meta.touched || meta.dirty) && (meta.error || !meta.valid) ? (
                <forms.errorRenderer error={meta.error} />
            ) : helperText || showMaxSize && `${value.length} / ${maxSize}`
        }
        error={(meta.touched || meta.dirty) && (!!meta.error || !meta.valid)}
        onPaste={useCallback(
            e => {
                if (onPaste)
                    // @ts-ignore
                    onPaste(e, onChange);
            },
            [onPaste, onChange],
        )}
        {...props}
    />
));

export const TextAreaField: FC<FieldRenderProps<string> & TextAreaProps> = ({
    input: { name, value, type, onChange, onBlur, onFocus, ...restInput },
    meta,
    helperText,
    showMaxSize,
    maxSize,
    onPaste,
    ...props
}) => (
    <Input
        name={name}
        value={value}
        type={type}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        inputProps={restInput}
        helperText={
            (meta.touched || meta.dirty) && (meta.error || !meta.valid) ? (
                <forms.errorRenderer error={meta.error} />
            ) : helperText || showMaxSize && `${value.length} / ${maxSize}`
        }
        error={(meta.touched || meta.dirty) && (!!meta.error || !meta.valid)}
        onPaste={useCallback(
            e => {
                if (onPaste)
                    // @ts-ignore
                    onPaste(e, onChange);
            },
            [onPaste, onChange],
        )}
        {...props}
    />
);

export const PasswordField: FC<InputFieldType> = props => {
    const [showPassword, setState] = useState(false);
    const toggle = useCallback(() => setState(old => !old), []);
    return (
        <InputField
            type={showPassword ? 'text' : 'password'}
            InputProps={{
                endAdornment: (
                    <InputAdornment position='end'>
                        <IconButton
                            onClick={toggle}
                            size='small'
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: '2rem',
                                },
                            }}
                        >
                            {showPassword ? <EyeClose24 /> : <Eye24 />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    );
};
